/**
 * Agency-logo rendition selection (#3417, D4-WLB-011).
 *
 * Split out of `components/AgencyLogo.tsx` so that module exports ONLY the
 * component (React Fast Refresh / `react-refresh/only-export-components`), and
 * so a caller that must branch BEFORE rendering -- `BrandedLockup`, which shows
 * the agency NAME when there is no logo -- can ask the same question the
 * component asks, instead of re-deriving it.
 *
 * `GET /api/v1/config/branding/` publishes three logo fields:
 *   - `logo_compact_url`    square/compact rendition, built for app chrome
 *   - `logo_letterhead_url` wide rendition (900x240), built for lockups/PDFs
 *   - `logo_url`            the generic one (the letterhead rendition when a
 *                           DB asset exists, else the env `AGENCY_LOGO_URL`)
 *
 * Each variant prefers its purpose-built rendition and falls back to the
 * generic URL, so an env-only deployment with no uploaded asset still shows a
 * logo rather than nothing.
 */
import type { Branding } from "../contexts/branding";

export type AgencyLogoVariant = "compact" | "letterhead";
export type AgencyLogoSurface = "dark" | "light" | "adaptive";
export type AgencyLogoSize = "sm" | "lg";

export function agencyLogoSrc(
  branding: Branding,
  variant: AgencyLogoVariant,
): string | null {
  const preferred =
    variant === "compact"
      ? branding.logo_compact_url
      : branding.logo_letterhead_url;
  return preferred || branding.logo_url || null;
}
